import moment from 'moment';
import pick from 'ramda/src/pick';
import find from 'ramda/src/find';
import propOr from 'ramda/src/propOr';

import {
  getScheduleFromPromo,
  isScheduledPromo,
  isUsableEveryNumberOfDays,
  getUsableEveryNumberOfDaysFromPromo,
  isUsableNumberOfTimes,
  getUsableNumberOfTimesFromPromo,
  isForServicesOrProducts,
  getServicesFromPromo,
  getProductsFromPromo,
  getSelectedShopIdsFromPromo,
} from './utils';
import { regularToMilitaryTime } from '../../../common/helpers/times';

export const fromApi = (apiPromo) => {
  const promo = pick(['id', 'code', 'value', 'createdAt', 'kind', 'redeemCount', 'totalRedeemedAmount'], apiPromo);
  const firstAppointmentRule = find(item => item.kind === 5, apiPromo.rules);
  if (firstAppointmentRule) {
    promo.firstAppointmentOnly = true;
  }

  const expirationDateRule = find(item => item.kind === 2, apiPromo.rules);
  if (expirationDateRule) {
    promo.expirationDate = expirationDateRule.endDate;
  }

  const oneTime = find(item => item.kind === 6, apiPromo.rules);

  if (oneTime) {
    promo.oneTime = true;
  }

  promo.schedule = getScheduleFromPromo(apiPromo);
  promo.isScheduled = isScheduledPromo(apiPromo);
  promo.usableEveryNumberOfDays = getUsableEveryNumberOfDaysFromPromo(apiPromo);
  promo.isUsableEveryNumberOfDays = isUsableEveryNumberOfDays(apiPromo);
  promo.usableNumberOfTimes = getUsableNumberOfTimesFromPromo(apiPromo);
  promo.isUsableNumberOfTimes = isUsableNumberOfTimes(apiPromo);
  promo.isForServicesOrProducts = isForServicesOrProducts(apiPromo);
  promo.services = getServicesFromPromo(apiPromo);
  promo.products = getProductsFromPromo(apiPromo);
  promo.shopIds = getSelectedShopIdsFromPromo(apiPromo);
  promo.isBrandPromo = apiPromo.isBrandPromo;

  return promo;
};

export const toApi = (formPromo, timezone, format) => {
  const data = pick(['code', 'value', 'kind'], formPromo);
  data.rules = [];
  if (formPromo.oneTime) {
    data.rules.push({ kind: 6 });
  }

  if (formPromo.firstAppointmentOnly) {
    data.rules.push({
      kind: 5,
    });
  }

  // legacy
  if (formPromo.expirationDate && !format) {
    data.rules.push({
      kind: 2,
      startDate: moment.tz(timezone).toDate(),
      endDate: moment.tz(formPromo.expirationDate, timezone).toDate(),
    });
  }

  if (formPromo.expirationDate && format) {
    const dateFormat = format === 'MM/DD/YYYY' ? 'MM-DD-YYYY' : 'DD-MM-YYYY';
    data.rules.push({
      kind: 2,
      startDate: moment.tz(timezone).toDate(),
      endDate: moment.tz(formPromo.expirationDate, dateFormat, timezone).toDate(),
    });
  }

  if (formPromo.isScheduled) {
    data.rules.push({
      kind: 8,
      schedule: formPromo.schedule.map((item) => {
        const [startHour, startMinute] = regularToMilitaryTime(item.startTime).split(':');
        const [finishHour, finishMinute] = regularToMilitaryTime(item.finishTime).split(':');

        return {
          ...pick(['weekDay', 'enabled'], item),
          startHour,
          startMinute,
          finishHour,
          finishMinute,
        };
      }),
    });
  }

  if (formPromo.isUsableEveryNumberOfDays) {
    data.rules.push({
      kind: 1,
      value: formPromo.usableEveryNumberOfDays,
    });
  }

  if (formPromo.isUsableNumberOfTimes) {
    data.rules.push({
      kind: 7,
      value: formPromo.usableNumberOfTimes,
    });
  }

  if (formPromo.isForServicesOrProducts) {
    // services are array of ids when editing a promo without changing services input
    const serviceIds = formPromo.services.map(service => propOr(service, 'id', service));
    const productIds = formPromo.products.map(product => propOr(product, 'id', product));
    data.rules.push({
      kind: 10,
      serviceIds,
      productIds,
    });
  }

  if (formPromo.isBrandPromo) {
    data.shopIds = formPromo.shops.filter(s => s.assigned).map(s => s.id);
  }

  return data;
};
